import { useState } from 'react'
import { Button, ButtonGroup } from '@enterprise-ui/canvas-ui-react'
import EnterpriseIcon, {
  InfoIcon,
  LinkIcon,
  CheckCircleIcon,
  DownloadIcon,
  CautionIcon,
} from '@enterprise-ui/icons'
import {
  MigrationContent,
  IntroText,
  UrlContainer,
  UrlLabel,
  UrlInputGroup,
  UrlInput,
  HintText,
  InstructionsContainer,
  InstructionCard,
  InstructionTitle,
  InstructionList,
  FooterInfo,
  TabsContainer,
  BenefitsList,
  BenefitItem,
  SectionTitle,
  HighlightBox,
  StepNumber,
  CompletionNote,
  AppIcon,
  OpenInBrowserLink,
  CollapsibleDetails,
  HighlightBoxSpaced,
  InstructionIntro,
  SupportText,
  SunsetWarningBox,
  MdmNoticeBox,
  BookmarkHero,
  BookmarkEmojiIcon,
} from './styles'

type Platform = 'ios' | 'android'

export default function MigrationPage({
  isInWebBrowser,
  isInOldAppStoreApp,
  deploymentMode = 'mdm',
  sunsetDate = null,
}: {
  isInWebBrowser?: boolean
  isInOldAppStoreApp?: boolean
  deploymentMode?: 'mdm' | 'manual'
  sunsetDate?: string | null
}) {
  const [selectedPlatform, setSelectedPlatform] = useState<Platform>('ios')
  const [copySuccess, setCopySuccess] = useState(false)
  const [copyNewUrlSuccess, setCopyNewUrlSuccess] = useState(false)

  const NEW_URL = 'https://trueyemobile.target.com/'

  function copyToClipboard() {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    })
  }

  function copyNewUrl() {
    navigator.clipboard.writeText(NEW_URL).then(() => {
      setCopyNewUrlSuccess(true)
      setTimeout(() => setCopyNewUrlSuccess(false), 2000)
    })
  }

  const myTVSCompletionNote = (
    <CompletionNote>
      <AppIcon src="/mytvs-small.png" alt="TruEye Mobile app icon" />
      The TruEye Mobile icon will appear on your home screen like a regular app!
    </CompletionNote>
  )

  // ── Manual browser-install steps (used as main content in 'manual' mode,
  //    or as a collapsible fallback in 'mdm' mode) ──────────────────────────
  const manualInstallSteps = (
    <>
      <HighlightBox>
        <EnterpriseIcon icon={InfoIcon} size="md" />
        <div>
          <strong>Quick Setup:</strong>{' '}
          {isInWebBrowser
            ? "You're already in the browser! Just follow Step 2 below to add TruEye Mobile to your home screen."
            : 'Copy the URL below, open it in your mobile browser (Safari or Chrome), and add to your home screen. It only takes 30 seconds!'}
        </div>
      </HighlightBox>

      {!isInWebBrowser && (
        <UrlContainer>
          <UrlLabel>
            <EnterpriseIcon icon={LinkIcon} size="sm" />
            <SectionTitle>Step 1: Open in Your Browser</SectionTitle>
          </UrlLabel>
          <HintText style={{ marginBottom: '16px' }}>
            <strong>Tap and hold</strong> the link below, then select "Open in
            Safari" (iOS) or "Open in Chrome" (Android)
          </HintText>
          <OpenInBrowserLink
            id="system-browser-link"
            href={window.location.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            📱 Tap &amp; Hold to Open in Browser
          </OpenInBrowserLink>
          <CollapsibleDetails>
            <summary>Or copy the URL manually</summary>
            <UrlInputGroup style={{ marginTop: '12px' }}>
              <UrlInput
                aria-label="myTVS web url"
                value={window.location.href}
                readOnly
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <Button
                onClick={copyToClipboard}
                type={copySuccess ? 'primary' : 'secondary'}
                aria-label="Copy URL to clipboard"
              >
                {copySuccess ? (
                  <>
                    <EnterpriseIcon icon={CheckCircleIcon} size="sm" />
                    Copied!
                  </>
                ) : (
                  'Copy URL'
                )}
              </Button>
            </UrlInputGroup>
          </CollapsibleDetails>
        </UrlContainer>
      )}

      {isInWebBrowser && (
        <UrlContainer>
          <UrlLabel className="completed">
            <EnterpriseIcon icon={CheckCircleIcon} size="sm" />
            <SectionTitle>Step 1: Get the URL</SectionTitle>
          </UrlLabel>
          <HintText>
            You're already viewing TruEye Mobile in your browser! Now just
            follow Step 2 below to add it to your home screen.
          </HintText>
        </UrlContainer>
      )}

      <InstructionsContainer>
        <SectionTitle>Step 2: Add to Home Screen</SectionTitle>
        <InstructionIntro>
          Choose your device type to see detailed instructions:
        </InstructionIntro>
        <TabsContainer>
          <ButtonGroup>
            <Button
              onClick={() => setSelectedPlatform('ios')}
              className={selectedPlatform === 'ios' ? 'selected-tab' : ''}
              type="secondary"
              aria-label="Show iOS instructions"
            >
              📱 iOS / Safari
            </Button>
            <Button
              onClick={() => setSelectedPlatform('android')}
              className={selectedPlatform === 'android' ? 'selected-tab' : ''}
              type="secondary"
              aria-label="Show Android instructions"
            >
              🤖 Android / Chrome
            </Button>
          </ButtonGroup>
        </TabsContainer>

        {selectedPlatform === 'ios' ? (
          <InstructionCard>
            <InstructionTitle>iOS (Safari) Instructions</InstructionTitle>
            <InstructionList>
              <li>
                <StepNumber>1</StepNumber>
                <p>
                  Open <strong>Safari</strong> on your iPhone or iPad
                </p>
              </li>
              <li>
                <StepNumber>2</StepNumber>
                <p>Paste or navigate to the URL above</p>
              </li>
              <li>
                <StepNumber>3</StepNumber>
                <p>
                  Tap the <strong>Share button</strong> (box with an arrow
                  pointing up) at the bottom
                </p>
              </li>
              <li>
                <StepNumber>4</StepNumber>
                <p>
                  Scroll down and tap <strong>"Add to Home Screen"</strong>
                </p>
              </li>
              <li>
                <StepNumber>5</StepNumber>
                <p>
                  Tap <strong>"Add"</strong> in the top right corner
                </p>
              </li>
            </InstructionList>
            {myTVSCompletionNote}
          </InstructionCard>
        ) : (
          <InstructionCard>
            <InstructionTitle>Android (Chrome) Instructions</InstructionTitle>
            <InstructionList>
              <li>
                <StepNumber>1</StepNumber>
                <p>
                  Open <strong>Chrome</strong> on your Android device
                </p>
              </li>
              <li>
                <StepNumber>2</StepNumber>
                <p>Paste or navigate to the URL above</p>
              </li>
              <li>
                <StepNumber>3</StepNumber>
                <p>
                  Tap the <strong>three dots menu</strong> (⋮) in the top right
                  corner
                </p>
              </li>
              <li>
                <StepNumber>4</StepNumber>
                <p>
                  Select <strong>"Add to Home screen"</strong> or{' '}
                  <strong>"Install app"</strong>
                </p>
              </li>
              <li>
                <StepNumber>5</StepNumber>
                <p>
                  Tap <strong>"Add"</strong> or <strong>"Install"</strong> to
                  confirm
                </p>
              </li>
            </InstructionList>
            {myTVSCompletionNote}
          </InstructionCard>
        )}
      </InstructionsContainer>
    </>
  )

  if (isInWebBrowser) {
    return (
      <MigrationContent>
        <BookmarkHero>
          <BookmarkEmojiIcon>🔖</BookmarkEmojiIcon>
          <SectionTitle style={{ textAlign: 'center', marginBottom: 0 }}>
            Update your bookmark
          </SectionTitle>
        </BookmarkHero>

        <IntroText>
          We're moving to a new home — swap your bookmark now so you don't lose
          access.
        </IntroText>

        <SunsetWarningBox>
          <EnterpriseIcon icon={CautionIcon} size="md" />
          <div>
            <strong>This URL is going away soon.</strong> Update your bookmark
            to the new address below before it's decommissioned.
          </div>
        </SunsetWarningBox>

        <UrlContainer>
          <UrlLabel>
            <EnterpriseIcon icon={LinkIcon} size="sm" />
            <SectionTitle>Your new TruEye URL</SectionTitle>
          </UrlLabel>
          <HintText style={{ marginBottom: '12px' }}>
            Tap <strong>Open new URL</strong> to visit it, then update your
            bookmark from there.
          </HintText>
          <OpenInBrowserLink
            href={NEW_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            🔗 Open new URL
          </OpenInBrowserLink>
          <CollapsibleDetails>
            <summary>Or copy the URL manually</summary>
            <UrlInputGroup style={{ marginTop: '12px' }}>
              <UrlInput
                aria-label="New TruEye URL"
                value={NEW_URL}
                readOnly
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <Button
                onClick={copyNewUrl}
                type={copyNewUrlSuccess ? 'primary' : 'secondary'}
                aria-label="Copy new URL to clipboard"
              >
                {copyNewUrlSuccess ? (
                  <>
                    <EnterpriseIcon icon={CheckCircleIcon} size="sm" />
                    Copied!
                  </>
                ) : (
                  'Copy'
                )}
              </Button>
            </UrlInputGroup>
          </CollapsibleDetails>
        </UrlContainer>

        <FooterInfo>
          <SectionTitle>What to expect</SectionTitle>
          <BenefitsList>
            <BenefitItem>
              <EnterpriseIcon icon={CheckCircleIcon} size="sm" />
              <div>
                <strong>One-time sign-in:</strong> You'll be asked to sign in
                once on the new URL — that's expected and only happens the first
                time
              </div>
            </BenefitItem>
            <BenefitItem>
              <EnterpriseIcon icon={CheckCircleIcon} size="sm" />
              <div>
                <strong>Everything carries over:</strong> Your cameras and
                favorites are exactly the same
              </div>
            </BenefitItem>
            <BenefitItem>
              <EnterpriseIcon icon={CheckCircleIcon} size="sm" />
              <div>
                <strong>Always current:</strong> The new URL always has the
                latest version — nothing to update
              </div>
            </BenefitItem>
          </BenefitsList>

          <SupportText>
            Need help? Contact support at <br />
            <a href="mailto:Targettech-TruEye-Mpls@Target.com">
              Targettech-TruEye-Mpls@Target.com
            </a>
          </SupportText>
        </FooterInfo>
      </MigrationContent>
    )
  }

  return (
    <MigrationContent>
      {/* ── MDM deployment: old app users ──────────────────────────────── */}
      {isInOldAppStoreApp && deploymentMode === 'mdm' && (
        <>
          <IntroText>Say hello to the next version of TruEye.</IntroText>

          {sunsetDate ? (
            <SunsetWarningBox>
              <EnterpriseIcon icon={CautionIcon} size="md" />
              <div>
                <strong>Heads up — this app retires on {sunsetDate}.</strong>{' '}
                Switch to the new TruEye from your home screen before then to
                keep things running smoothly.
              </div>
            </SunsetWarningBox>
          ) : (
            <MdmNoticeBox>
              <EnterpriseIcon icon={CheckCircleIcon} size="md" />
              <div>
                <strong>Nothing to download.</strong> The new TruEye arrives on
                your device automatically — just look for the new icon.
              </div>
            </MdmNoticeBox>
          )}

          <HighlightBox>
            <AppIcon
              src="/mytvs.png"
              alt="New TruEye app icon"
              style={{ width: 56, height: 56, borderRadius: 12 }}
            />
            <div>
              <strong>Spot the new icon:</strong> The new TruEye Mobile has a
              refreshed look (pictured left) and may land on a{' '}
              <strong>different home screen page</strong> than today's app.
              Swipe through your screens to find it.
            </div>
          </HighlightBox>

          <FooterInfo>
            <SectionTitle>What's Different?</SectionTitle>
            <BenefitsList>
              <BenefitItem>
                <EnterpriseIcon icon={CheckCircleIcon} size="sm" />
                <div>
                  <strong>Always current:</strong> Improvements roll out
                  automatically — no updates to install
                </div>
              </BenefitItem>
              <BenefitItem>
                <EnterpriseIcon icon={CheckCircleIcon} size="sm" />
                <div>
                  <strong>Quick sign-in:</strong> You'll be asked to sign in
                  once when you first open the new app — that's expected
                </div>
              </BenefitItem>
              <BenefitItem>
                <EnterpriseIcon icon={CheckCircleIcon} size="sm" />
                <div>
                  <strong>Everything in its place:</strong> Your cameras and
                  favorites carry over just as they are today
                </div>
              </BenefitItem>
            </BenefitsList>

            <SupportText>
              Need help? Contact support at <br />
              <a href="mailto:Targettech-TruEye-Mpls@Target.com">
                Targettech-TruEye-Mpls@Target.com
              </a>
            </SupportText>
          </FooterInfo>
        </>
      )}

      {/* ── Manual / self-service path (web browser or manual mode) ─────── */}
      {(deploymentMode === 'manual' ||
        (!isInOldAppStoreApp && deploymentMode === 'mdm')) && (
        <>
          <IntroText>
            We're moving to a Progressive Web App for a better, faster
            experience! No app store needed - just add TruEye Mobile directly to
            your home screen.
          </IntroText>

          {manualInstallSteps}

          <FooterInfo>
            <SectionTitle>Why Make This Switch?</SectionTitle>
            <BenefitsList>
              <BenefitItem>
                <EnterpriseIcon icon={CheckCircleIcon} size="sm" />
                <div>
                  <strong>Always Up-to-Date:</strong> Get new features instantly
                  without app store downloads
                </div>
              </BenefitItem>
              <BenefitItem>
                <EnterpriseIcon icon={CheckCircleIcon} size="sm" />
                <div>
                  <strong>Faster Performance:</strong> Optimized web experience
                  with better loading times
                </div>
              </BenefitItem>
              <BenefitItem>
                <EnterpriseIcon icon={CheckCircleIcon} size="sm" />
                <div>
                  <strong>Same Experience:</strong> Works exactly like the app,
                  right from your home screen
                </div>
              </BenefitItem>
              <BenefitItem>
                <EnterpriseIcon icon={CheckCircleIcon} size="sm" />
                <div>
                  <strong>No Storage Hassles:</strong> Uses less device storage
                  than traditional apps
                </div>
              </BenefitItem>
            </BenefitsList>

            {isInOldAppStoreApp && (
              <HighlightBoxSpaced>
                <EnterpriseIcon icon={DownloadIcon} size="md" />
                <div>
                  <strong>Successfully added TruEye Mobile?</strong> Once you've
                  successfully added TruEye Mobile to your home screen, you can
                  safely delete the old app-store version to free up space!
                </div>
              </HighlightBoxSpaced>
            )}

            {sunsetDate && (
              <SunsetWarningBox>
                <EnterpriseIcon icon={CautionIcon} size="md" />
                <div>
                  <strong>Legacy app removal on {sunsetDate}:</strong> Make sure
                  you've switched to the PWA before this date.
                </div>
              </SunsetWarningBox>
            )}

            <SupportText>
              Need help? Contact support at <br />
              <a href="mailto:Targettech-TruEye-Mpls@Target.com">
                Targettech-TruEye-Mpls@Target.com
              </a>
            </SupportText>
          </FooterInfo>
        </>
      )}
    </MigrationContent>
  )
}
